import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, ContextualNotification, FieldLabel, Grid, InlineNotification, TextInput, Typography, Field, Modal } from '@blueprint/ui';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { clearErrorMessage, validateCredentials, clearSuccessMessage } from '../../actions';
import { isEmailValid } from '../../functions/isEmailValid';
import { InlineNotificationComponent } from '../Notification/InlineNotificationComponent';
import CaptchaErrorModal from '../CaptchaErrorModal/CaptchaErrorModal';
import Recaptcha from '../Recaptcha/Recaptcha';
import CONSTANTS from '../../constants/constants';
import { setCaptchaHeader } from '../../actions/index';

const StyledTypography = styled(Typography)`
    color: #333333;
    text-decoration: underline;
    &:hover {
        cursor: pointer;
    }
`;

const StyledLink = styled(Link)`
    color: #333333;
    text-decoration: underline;
    &:hover {
        cursor: pointer;
        color: #333333;
    }
`;

export const RegisteredLogin = ({ location }) => {
    const [open, setOpen] = useState(false);
    const [forgotPasswordTimeoutCount, setForgotPasswordTimeoutCount] = useState(0);
    let showModal = location.state === undefined ? null : location.state.showModal;
    let modalText = location.state === undefined ? null : location.state.modalMessage;

    if (showModal && !open && forgotPasswordTimeoutCount === 0) {
        setOpen(true);
        setForgotPasswordTimeoutCount((prev) => prev + 1);
    }

    const globalState = useSelector((state) => state);
    var CAPTCHA_ENABLED;
    if (process.env.NODE_ENV === 'development') {
        CAPTCHA_ENABLED = CONSTANTS.CAPTCHA_ENABLED;
    } else {
        CAPTCHA_ENABLED = globalState.beneDetails.captchaConfig.recaptchaEnabled;
    }

    const { t } = useTranslation();
    const history = useHistory();
    const dispatch = useDispatch();
    const [gridHeight, setGridHeight] = useState();
    const topMarginGridRef = useRef(0);

    const footerHeight = globalState.beneDetails.footerHeight;

    const [showError, setShowError] = useState(false);
    const [loginError, setLoginError] = useState('');

    const [emailId, setEmailId] = useState('');
    const [password, setPassword] = useState('');

    const [captcha, setCaptcha] = useState(null);
    const [captchaValidationError, setCaptchaValidationError] = useState(false);

    const [captchaErrorModal, setCaptchaErrorModal] = useState(false);

    const [errorFlags, setErrorFlags] = useState({
        emailId: false,
        password: false,
        captcha: false,
    });

    const [errorMessages, setErrorMessages] = useState({
        emailId: '',
        password: '',
        captcha: '',
    });

    const [question, setQuestion] = useState({ id: null, qid: null, question: null });
    const [answer, setAnswer] = useState('');

    const PAYMENT_LIST = process.env.REACT_APP_PAYMENT_LIST;
    const MOBILE_CONFIRMATION = process.env.REACT_APP_MOBILE_CONFIRMATION;
    const GUEST_REGISTRATION = process.env.REACT_APP_GUEST_REGISTRATION;

    useEffect(() => {
        if (topMarginGridRef.current.clientHeight) {
            setGridHeight(topMarginGridRef.current.clientHeight);
        }
    }, [topMarginGridRef.current.clientHeight, question, globalState.beneDetails.securityQuestion]);

    useEffect(() => {
        console.log('Logging global state before attempt to push inside from Registered Tab', globalState);

        if (globalState.beneDetails.details.isLogged && globalState.beneDetails.registeredUser) {
            dispatch(clearErrorMessage());
            dispatch({ type: 'RESET_SECURITY_QUESTION', payload: { id: null, qid: null, question: null } });
            if (
                globalState.beneDetails.mobileDetails.mobileConfirmation === false &&
                globalState.beneDetails.mobileDetails.maskedMobileNo !== null &&
                globalState.beneDetails.mobileDetails.maskedMobileNo !== ''
            ) {
                history.push(MOBILE_CONFIRMATION);
            } else {
                history.push(PAYMENT_LIST);
            }
        }

        if (globalState.beneDetails.details.isLogged && !globalState.beneDetails.registeredUser) {
            dispatch(clearErrorMessage());
            dispatch({ type: 'RESET_SECURITY_QUESTION', payload: { id: null, qid: null, question: null } });
            history.push(GUEST_REGISTRATION);
        }

        if (globalState.beneDetails.details && globalState.beneDetails.details.error) {
            console.log('Logging Error Message from Login Screen', globalState.beneDetails.error.message);
            console.log('Logging Error Status from Login Screen', globalState.beneDetails.error.status);
        }

        if (globalState.beneDetails.error && globalState.beneDetails.error.status === 401 && globalState.beneDetails.error.message) {
            setLoginError(globalState.beneDetails.error.message);
            setShowError(true);
            setCaptchaValidationError(true);
        }

        if (!globalState.beneDetails.captchaToken) {
            setCaptcha(null);
        }
    }, [globalState, history, PAYMENT_LIST, GUEST_REGISTRATION, dispatch]);

    useEffect(() => {
        if (globalState.beneDetails.securityQuestion) {
            setQuestion(globalState.beneDetails.securityQuestion);
            setAnswer('');
        }
    }, [globalState.beneDetails.securityQuestion]);

    useEffect(() => {
        return () => {
            dispatch(clearErrorMessage());
            dispatch(clearSuccessMessage());
        };
    }, []);

    const handleFormSubmit = () => {
        let areDetailsValid = true;

        let errorFlags = {
            emailId: false,
            password: false,
            captcha: false,
        };

        let errorMessages = {
            emailId: '',
            password: '',
            captcha: '',
        };

        // Validate email
        if (emailId.trim() === '' || emailId === null) {
            errorFlags.emailId = true;
            errorMessages.emailId = 'Please enter a valid email address';
            areDetailsValid = false;
        } else {
            if (isEmailValid(emailId)) {
                errorFlags.emailId = false;
                errorMessages.emailId = '';
            } else {
                errorFlags.emailId = true;
                errorMessages.emailId = 'Please enter a valid email address';
                areDetailsValid = false;
            }
        }

        // Validate password
        if (password.trim() === '' || password === null) {
            errorFlags.password = true;
            errorMessages.password = 'Please enter a valid password';
            areDetailsValid = false;
        } else {
            errorFlags.password = false;
            errorMessages.password = '';
        }

        // Validate captcha
        if (CAPTCHA_ENABLED) {
            if (captcha) {
                errorFlags.captcha = false;
                errorMessages.captcha = '';
            } else {
                errorFlags.captcha = true;
                errorMessages.captcha = 'Please verify captcha';
                areDetailsValid = false;
            }
        }

        console.log('Setting error messages in state');
        setErrorMessages({
            emailId: errorMessages.emailId,
            password: errorMessages.password,
            captcha: errorMessages.captcha,
        });

        console.log('Setting error flags in state');
        setErrorFlags({
            emailId: errorFlags.emailId,
            password: errorFlags.password,
            captcha: errorFlags.captcha,
        });

        if (areDetailsValid) {
            console.log('Validations passed, Dispatching action');
            dispatch(clearSuccessMessage());
            dispatch(validateCredentials(emailId, password, 'REGISTERED', question.id, answer));
        } else {
            console.log('Validations failed');
        }
    };

    const validateEmail = () => {
        if (emailId.trim() === '' || emailId === null) {
            setErrorFlags({ ...errorFlags, emailId: true });
            setErrorMessages({ ...errorMessages, emailId: 'Please enter a valid email address' });
        } else {
            if (isEmailValid(emailId)) {
                setErrorFlags({ ...errorFlags, emailId: false });
                setErrorMessages({ ...errorMessages, emailId: '' });
            } else {
                setErrorFlags({ ...errorFlags, emailId: true });
                setErrorMessages({ ...errorMessages, emailId: 'Please enter a valid email address' });
            }
        }
    };

    const validatePassword = () => {
        if (password.trim() === '' || password === null) {
            setErrorFlags({ ...errorFlags, password: true });
            setErrorMessages({ ...errorMessages, password: 'Please enter a valid password' });
        } else {
            setErrorFlags({ ...errorFlags, password: false });
            setErrorMessages({ ...errorMessages, password: '' });
        }
    };

    const questionJSX = (
        <>
            <Field name='security-question'>
                <Grid xs={12} item>
                    <Box marginTop={['5px', '5px', '5px', '10px']}>
                        <FieldLabel>Security question</FieldLabel>
                    </Box>
                </Grid>
                <Grid xs={12} item>
                    <TextInput value={globalState.beneDetails.securityQuestion.question} disabled />
                </Grid>
            </Field>
            <Field name='security-answer'>
                <Grid xs={12} item>
                    <Box marginTop={['5px', '5px', '5px', '10px']}>
                        <FieldLabel>Security answer</FieldLabel>
                    </Box>
                </Grid>
                <Grid xs={12} item>
                    <TextInput type='password' placeholder='Security answer' onChange={(event) => setAnswer(event.value)} value={answer} />
                </Grid>
            </Field>
        </>
    );

    useEffect(() => {
        const captchaContainer = document.getElementById('recaptcha');
        setTimeout(() => {
            if (captchaContainer) {
                if (!captchaContainer.hasChildNodes() && errorFlags.captcha) {
                    setCaptchaErrorModal(true);
                } else {
                    setCaptchaErrorModal(false);
                }
            }
        }, 200);
    }, [errorFlags, globalState.beneDetails.captchaConfig]);

    return (
        <>
            <Box ref={topMarginGridRef}>
                <Box style={{ paddingTop: '130px', paddingBottom: `${footerHeight + 20}px`, display: 'flex', justifyContent: 'center' }}>
                    <Box
                        style={{
                            backgroundColor: 'white',
                            padding: '20px 2.5vw 20px 2.5vw',
                            maxWidth: '600px',
                        }}
                    >
                        <Box>
                            <Grid xs={12} item alignItems='center' justify='space-between'>
                                <Typography variant='h1' className='page-title' tabIndex='-1'>
                                    {t('loginPage.hsbc_bm')}
                                </Typography>
                            </Grid>
                            {captchaErrorModal ? (
                                <Grid item xs={12}>
                                    <Box mb={2} marginTop={4}>
                                        <ContextualNotification
                                            id='contextual-error'
                                            title=''
                                            variant='error'
                                            iconAriaLabel='error'
                                            onClose={() => {
                                                setCaptchaErrorModal(false);
                                                setErrorFlags({ ...errorFlags, captcha: false });
                                                dispatch(clearErrorMessage());
                                            }}
                                            aria-label={t('loginPage.captcha_error')}
                                        >
                                            {t('loginPage.captcha_error')}
                                        </ContextualNotification>
                                    </Box>
                                </Grid>
                            ) : null}
                            {showError ? (
                                <Grid item xs={12}>
                                    <Box mb={2} marginTop={4} maxWidth='600px'>
                                        <ContextualNotification
                                            id='contextual-error'
                                            title=''
                                            variant='error'
                                            iconAriaLabel='error'
                                            onClose={() => {
                                                setShowError(false);
                                                dispatch(clearErrorMessage());
                                            }}
                                            aria-label={loginError}
                                        >
                                            {loginError}
                                        </ContextualNotification>
                                    </Box>
                                </Grid>
                            ) : null}
                            {globalState.beneDetails.successMessage.status ? (
                                <Grid item xs={12}>
                                    <Box mb={2} marginTop={4} maxWidth='600px'>
                                        <ContextualNotification
                                            id='contextual-success'
                                            title=''
                                            variant='success'
                                            iconAriaLabel='success'
                                            onClose={() => {
                                                dispatch(clearSuccessMessage());
                                            }}
                                        >
                                            {globalState.beneDetails.successMessage.statusText}
                                        </ContextualNotification>
                                    </Box>
                                </Grid>
                            ) : null}
                            <Grid xs={12} item>
                                <Box marginTop={['5px', '5px', '5px', '5px']}>
                                    <StyledLink
                                        style={{ fontSize: '14px' }}
                                        onClick={() => {
                                            history.push('/');
                                            dispatch(clearErrorMessage());
                                            dispatch(clearSuccessMessage());
                                            dispatch({ type: 'RESET_SECURITY_QUESTION', payload: { id: null, qid: null, question: null } });
                                        }}
                                    >
                                        {t('loginPage.new_user')}
                                    </StyledLink>
                                </Box>
                            </Grid>
                            <Grid item>
                                <Box marginTop={['5px', '5px', '5px', '5px']}>
                                    <StyledLink
                                        tabIndex='0'
                                        style={{ fontSize: '14px' }}
                                        onClick={() => {
                                            history.push('/forgot-password');
                                        }}
                                    >
                                        {t('loginPage.forgot_link')}
                                    </StyledLink>
                                </Box>
                            </Grid>
                            <Field name='email-id'>
                                <Grid xs={12} item>
                                    <Box marginTop={['5px', '5px', '5px', '10px']}>
                                        <FieldLabel>{t('loginPage.email')}</FieldLabel>
                                    </Box>
                                </Grid>
                                <Grid xs={12} item>
                                    <TextInput
                                        placeholder='Email address'
                                        onChange={(event) => {
                                            setEmailId(event.value);
                                        }}
                                        onBlur={() => {
                                            validateEmail();
                                        }}
                                        aria-describedby='contextual-error email-inline-error'
                                    />
                                </Grid>
                            </Field>
                            {errorFlags.emailId ? (
                                <Grid item xs={12}>
                                    <InlineNotificationComponent id='email-inline-error' title='' content={errorMessages.emailId} />
                                </Grid>
                            ) : null}
                            <Field name='password'>
                                <Grid xs={12} item>
                                    <Box marginTop={['5px', '5px', '5px', '10px']}>
                                        <FieldLabel>{t('loginPage.password')}</FieldLabel>
                                    </Box>
                                </Grid>
                                <Grid xs={12} item>
                                    <TextInput
                                        type='password'
                                        placeholder='Password'
                                        onChange={(event) => setPassword(event.value)}
                                        onBlur={() => {
                                            validatePassword();
                                        }}
                                        aria-describedby='contextual-error password-inline-error'
                                    />
                                </Grid>
                            </Field>
                            {errorFlags.password ? (
                                <Grid item xs={12}>
                                    <InlineNotificationComponent id='password-inline-error' title='' content={errorMessages.password} />
                                </Grid>
                            ) : null}
                            {question.id ? questionJSX : null}
                            {CAPTCHA_ENABLED ? (
                                <>
                                    <Grid container>
                                        <Box marginTop='1em'>
                                            <Recaptcha
                                                onVerification={(token) => {
                                                    // Set header setHeaders({ 'X-HSBC-ReCaptcha-Response': token });
                                                    console.log('Got a new Captcha token', token);
                                                    dispatch(setCaptchaHeader(token));
                                                    setCaptcha(token);
                                                    setCaptchaValidationError(false);
                                                    setErrorFlags({ ...errorFlags, captcha: false });
                                                }}
                                                onExpiry={() => {
                                                    // Happens when timed out
                                                    setCaptcha(null);
                                                }}
                                                resetCaptcha={captchaValidationError} // Boolean variable that indicates if there was error in captcha
                                                action='onVerification'
                                            />
                                        </Box>
                                    </Grid>
                                    {errorFlags.captcha ? (
                                        <Grid container>
                                            <Box>
                                                <InlineNotificationComponent id='login-captcha-error' title='' content={errorMessages.captcha} />
                                            </Box>
                                        </Grid>
                                    ) : null}
                                </>
                            ) : null}
                            <Grid xs={12} container direction='row-reverse'>
                                <Box marginTop='1em'>
                                    <Button
                                        aria-label='Log on button'
                                        id='guest-log-on'
                                        fontSize='body1'
                                        variant='light.primary'
                                        onClick={() => {
                                            handleFormSubmit();
                                        }}
                                    >
                                        {t('loginPage.signin')}
                                    </Button>
                                </Box>
                            </Grid>
                        </Box>
                    </Box>
                </Box>
            </Box>
            <Modal
                open={open}
                onHide={() => setOpen(false)}
                closeIcon={true}
                onClickCloseIcon={() => setOpen(false)}
                size='small'
                activeFocusTrap={true}
                aria-labelledby='title1'
                minHeight='230px'
            >
                <Box display='flex' justifyContent='center' width='100%' marginTop='10%'>
                    <Box>
                        <Typography variant='h4' id='paymentsList-typo-modal-text' fontWeight='bold'>
                            {modalText}
                        </Typography>
                    </Box>
                </Box>
                <Box
                    position='absolute'
                    bottom='0'
                    py='1em'
                    width='calc(100% - 80px)'
                    display='flex'
                    justifyContent='center'
                    borderTop='1px solid #333'
                >
                    <Button
                        variant='light.primary'
                        onClick={() => {
                            setOpen(false);
                        }}
                    >
                        OK
                    </Button>
                </Box>
            </Modal>
        </>
    );
};
